import React, { useEffect, useState } from 'react'
import { getDocuments, fetchDocumentBlob } from '@/api/tapResources'
import { TapDocument } from '@/types/index'
import { FileText, Download } from 'lucide-react'

// Feature #190 — compact widget showing Resume-type documents on the Summary tab.
interface ResumeWidgetProps {
  applicationId: string
}

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-white)',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '8px',
  padding: '16px',
  marginBottom: '16px',
  width: '260px',
  minHeight: '120px',
}

const ResumeWidget: React.FC<ResumeWidgetProps> = ({ applicationId }) => {
  const [resumes, setResumes] = useState<TapDocument[]>([])
  const [loading, setLoading] = useState(true)
  // Bug #234: per-row "opening…" state so the user knows a click is in
  // flight while we fetch a token via the proxy endpoint.
  const [openingDocId, setOpeningDocId] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const docs = await getDocuments(applicationId)
        if (!cancelled) {
          setResumes(docs.filter((d) => d.kind === 'Resume'))
        }
      } catch {
        // silent — widget is supplementary
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [applicationId])

  const isPending = (d: TapDocument): boolean =>
    !d.storageUrl || d.storageUrl.startsWith('pending://')

  // Bug #234: previously this widget rendered an `<a href={doc.storageUrl}>`
  // pointing straight at the raw Azure Blob URL, which fails with a token
  // error for any user without direct AD blob access. Mirror the
  // DocumentsTab behaviour and proxy through the backend's /content
  // endpoint instead — the proxy attaches the right credentials and
  // returns a short-lived blob URL we can open in a new tab.
  const handleOpen = async (doc: TapDocument) => {
    if (isPending(doc)) return
    setOpeningDocId(doc.id)
    try {
      const url = await fetchDocumentBlob(doc.storageUrl, applicationId, doc.id)
      window.open(url, '_blank')
    } catch {
      alert('Failed to open resume')
    } finally {
      setOpeningDocId(null)
    }
  }

  if (loading) {
    return (
      <div style={cardStyle}>
        <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)' }}>Loading…</div>
      </div>
    )
  }

  return (
    <div style={cardStyle}>
      <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: 600, color: 'var(--color-dark-grey)', textTransform: 'uppercase' }}>
        Resumes
      </h4>
      {resumes.length === 0 ? (
        <div style={{ fontSize: '13px', color: 'var(--color-dark-grey)' }}>No resumes uploaded.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {resumes.map((doc) => (
            <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FileText size={16} style={{ color: 'var(--color-dark-grey)', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {doc.filename}
                </div>
              </div>
              {isPending(doc) ? (
                <span style={{ fontSize: '11px', color: 'var(--color-dark-grey)', opacity: 0.5 }}>Pending</span>
              ) : (
                <button
                  onClick={() => handleOpen(doc)}
                  disabled={openingDocId === doc.id}
                  title="Open"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: openingDocId === doc.id ? 'wait' : 'pointer',
                    padding: 0,
                    color: 'var(--color-magenta)',
                    flexShrink: 0,
                  }}
                >
                  <Download size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default ResumeWidget
